<template>
  <v-container fluid>
    <v-flex xs10 v-if="eventsArray">
      <v-flex mt-3>
        <h2>System Notification</h2>
        <v-layout
          v-for="(item, index) in eventsArray"
          :key='`statusDisplayNames-${index}-${item.value}`'
        >
          <v-flex class="my-4" xs4 v-html="item.name"/>
          <v-flex xs5>
          <email-select
            :displayLabel="false"
            :settingName="item.name"
            :arrayEmailTemplates="item.emailTemplates"
            :action="item.action"
            :value="pathOr('', [`${item.value}`], getEmailTemplatesSettings)"
            v-if="item.status"
          />
          </v-flex>
          <v-flex xs2>
          <v-switch
            v-model="item.status"
            @change="onChangeEnableStatus(item.status, item.action)"
          />
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex mt-3>
        <h2>Awards Notification</h2>
        <template v-if="getSendGridTemplates">
          <v-layout v-for="(item, index) in getAttendeeVoteReminders" :key="`voteReminders-${index}-${item.emailTemplateId}`">
            <v-flex class="my-4" xs4 v-html="item.awardsStatus"/>
            <v-flex xs3>
              <v-select
                :items="getSendGridVersions"
                item-text="name"
                item-value="templateId"
                v-model="attendeeVoteReminders[index]"
                @input="changeAwardsTemplateKey(attendeeVoteReminders[index], item.awardsStatus)"
                label="Choose Email Template"
                autocomplete
              />
            </v-flex>
          </v-layout>
        </template>
      </v-flex>
    </v-flex>
  </v-container>
</template>

<script>
import EmailSelect from './emailSelect'
import { mapGetters, mapActions } from 'vuex'
import mixins from '@/utils/mixins'
import { pathOr, prop, propEq } from 'ramda'

export default {
  name: 'EmailTemplatesSettings',
  mixins: [mixins.emailTemplates],
  components: { EmailSelect },
  data () {
    return {
      eventsArray: null,
      attendeeVoteReminders: []
    }
  },
  computed: {
    ...mapGetters([
      'getEmailTemplatesSettings',
      'getAttendeeVoteReminders',
      'getSendGridTemplates'
    ]),
    getSendGridVersions () {
      return this.getSendGridTemplates.map(template => {
        if (template.versions.length > 0) {
          return template.versions.find(version => version.isActive === true)
        } else {
          return {}
        }
      }).filter(item => !!item.templateId)
    },
    isDataReady () {
      return !!(this.getEmailTemplates.length && this.getEmailTemplatesSettings)
    }
  },
  watch: {
    isDataReady: {
      handler (newVal) {
        if (!newVal) return
        this.eventsArray = [
          {
            name: 'Attendee confirmation',
            emailTemplates: this.attendeeConfirmationTemplatesList,
            action: 'updateAttendeeConfirmationTemplate',
            value: 'attendeeConfirmationTemplate',
            status: !!this.getEmailTemplatesSettings['attendeeConfirmationTemplate']
          },
          {
            name: 'Attendee message',
            emailTemplates: this.attendeeMessageTemplatesList,
            action: 'updateAttendeeMessageTemplate',
            value: 'attendeeMessageTemplate',
            status: !!this.getEmailTemplatesSettings['attendeeMessageTemplate']
          },
          {
            name: 'Attendee to agenda session admin message',
            emailTemplates: this.attendeeMessageTemplatesList,
            action: 'updateAttendeeToAgendaAdminMessageTemplate',
            value: 'attendeeToAgendaSessionAdminMessageTemplate',
            status: !!this.getEmailTemplatesSettings['attendeeToAgendaSessionAdminMessageTemplate']
          },
          {
            name: 'Attendee to speaker message',
            emailTemplates: this.attendeeMessageTemplatesList,
            action: 'updateAttendeeToSpeakerMessageTemplate',
            value: 'attendeeToSpeakerMessageTemplate',
            status: !!this.getEmailTemplatesSettings['attendeeToSpeakerMessageTemplate']
          },
          {
            name: 'Attendee to blitz participant message',
            emailTemplates: this.attendeeMessageTemplatesList,
            action: 'updateAttendeeToParticipantMessageTemplate',
            value: 'attendeeToBlitzParticipantMessageTemplate',
            status: !!this.getEmailTemplatesSettings['attendeeToBlitzParticipantMessageTemplate']
          },
          {
            name: 'Blitz submitted',
            emailTemplates: this.blitzSubmittedTemplatesList,
            action: 'updateBlitzSubmittedTemplate',
            value: 'blitzSubmitedTemplate',
            status: !!this.getEmailTemplatesSettings['blitzSubmitedTemplate']
          },
          {
            name: 'Contact us',
            emailTemplates: this.contactUsTemplatesList,
            action: 'updateContactUsTemplate',
            value: 'contactUsTemplate',
            status: !!this.getEmailTemplatesSettings['contactUsTemplate']
          },
          {
            name: 'Enable video request',
            emailTemplates: this.enableVideoRequestTemplatesList,
            action: 'updateEnableVideoRequestTemplate',
            value: 'enableVideoRequestTemplate',
            status: !!this.getEmailTemplatesSettings['enableVideoRequestTemplate']
          },
          {
            name: 'Invoice refund sending',
            emailTemplates: this.invoiceRefundSendingTemplatesList,
            action: 'updateInvoiceRefundSendingTemplate',
            value: 'invoiceRefundSendingTemplate',
            status: !!this.getEmailTemplatesSettings['invoiceRefundSendingTemplate']
          },
          {
            name: 'Invoice sending',
            emailTemplates: this.invoicesTemplatesList,
            action: 'updateInvoiceSendingTemplate',
            value: 'invoiceSendingTemplate',
            status: !!this.getEmailTemplatesSettings['invoiceSendingTemplate']
          },
          {
            name: 'IPN refund sending',
            emailTemplates: this.IPNRefundSendingTemplatesList,
            action: 'updateIPNRefundSendingTemplate',
            value: 'ipnRefundSendingTemplate',
            status: !!this.getEmailTemplatesSettings['ipnRefundSendingTemplate']
          },
          {
            name: 'IPN sending',
            emailTemplates: this.IPNRefundSendingTemplatesList,
            action: 'updateIPNSendingTemplate',
            value: 'ipnSendingTemplate',
            status: !!this.getEmailTemplatesSettings['ipnSendingTemplate']
          },
          {
            name: 'Listing submitted',
            emailTemplates: this.listingSubmittedTemplatesList,
            action: 'updateListingSubmittedTemplate',
            value: 'listingSubmitedTemplate',
            status: !!this.getEmailTemplatesSettings['listingSubmitedTemplate']
          },
          {
            name: 'Reset password',
            emailTemplates: this.forgotPasswordRequestTemplatesList,
            action: 'updateResetPasswordTemplate',
            value: 'resetPasswordTemplate',
            status: !!this.getEmailTemplatesSettings['resetPasswordTemplate']
          }
        ]
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions([
      'fetchGeneralSettings',
      'fetchAttendeeVoteReminders',
      'fetchSendGridTemplates',
      'updateAttendeeVoteReminder',
      'updateAttendeeConfirmationTemplate',
      'updateAttendeeMessageTemplate',
      'updateBlitzSubmittedTemplate',
      'updateContactUsTemplate',
      'updateEnableVideoRequestTemplate',
      'updateInvoiceRefundSendingTemplate',
      'updateInvoiceSendingTemplate',
      'updateListingSubmittedTemplate',
      'updateResetPasswordTemplate',
      'updateIPNSendingTemplate',
      'updateIPNRefundSendingTemplate',
      'updateAttendeeToAgendaAdminMessageTemplate',
      'updateAttendeeToParticipantMessageTemplate',
      'updateAttendeeToSpeakerMessageTemplate'
    ]),
    pathOr,
    async onChangeEnableStatus (isEnabledStatus, action) {
      if (!isEnabledStatus) {
        try {
          await this[action]({ templateName: null })
          await this.fetchGeneralSettings()
        } catch (err) {
          throw err
        }
      }
    },
    async changeAwardsTemplateKey (emailId, status) {
      try {
        const payload = {
          awardsStatus: status,
          emailTemplateId: emailId
        }
        await this.updateAttendeeVoteReminder(payload)
      } catch (err) {
        throw err
      }
    }
  },
  async mounted () {
    try {
      await Promise.all([
        this.fetchAttendeeVoteReminders(),
        this.fetchSendGridTemplates()
      ])
      if (!this.getEmailTemplatesSettings) await this.fetchGeneralSettings()
      this.attendeeVoteReminders = this.getAttendeeVoteReminders.map(reminder => {
        const emailTemplateName = this.getSendGridVersions.find(template => template.templateId === reminder.emailTemplateId)
        return {
          name: pathOr('', ['name'], emailTemplateName),
          templateId: reminder.emailTemplateId
        }
      })
    } catch (err) {
      console.error(err)
    }
  }
}
</script>




// WEBPACK FOOTER //
// src/components/pages/EmailNotificationsSettings/EmailTemplatesSettings/index.vue